// SOS audio beacon — Morse pattern · · · − − − · · · at 880 Hz using Web Audio
// API. ZERO external files. Idempotent start/stop. Cleanup-safe.
//
// Morse timings (one "unit" = 200ms by default):
//   dit (short):    1 unit of tone + 1 unit silence
//   dah (long):     3 units tone  + 1 unit silence
//   between letters: additional 2 units silence (total 3)
//   between words:  additional 6 units silence (total 7)
//
// SOS = ... --- ... = [S][O][S]

import { unlockAudio } from "./voice";

const DEFAULT_UNIT_MS = 200;
const DEFAULT_FREQ = 880;

// S  O  S pattern — true = tone, length in units
const SOS_SYMBOLS = [
  { tone: 1 }, { silence: 1 }, { tone: 1 }, { silence: 1 }, { tone: 1 }, // S
  { silence: 3 },
  { tone: 3 }, { silence: 1 }, { tone: 3 }, { silence: 1 }, { tone: 3 }, // O
  { silence: 3 },
  { tone: 1 }, { silence: 1 }, { tone: 1 }, { silence: 1 }, { tone: 1 }, // S
  { silence: 7 }, // word gap before next loop
];

export class SOSBeacon {
  constructor({ freq = DEFAULT_FREQ, unitMs = DEFAULT_UNIT_MS } = {}) {
    this.freq = freq;
    this.unitMs = unitMs;
    this._ctx = null;
    this._gain = null;
    this._osc = null;
    this._running = false;
    this._scheduledUntil = 0;
    this._nextScheduleTimer = null;
  }

  isRunning() {
    return this._running;
  }

  /**
   * Start the beacon. MUST be called from a user gesture (click/tap) — we
   * invoke `unlockAudio()` first so iOS Safari unlocks the audio stack.
   */
  start() {
    if (this._running) return;
    try {
      unlockAudio();
    } catch (_e) {
      /* ignore */
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return; // silently no-op in browsers without Web Audio
    this._ctx = new AC();
    this._gain = this._ctx.createGain();
    this._gain.gain.value = 0; // start silent, ramp per schedule
    this._gain.connect(this._ctx.destination);
    this._osc = this._ctx.createOscillator();
    this._osc.type = "sine";
    this._osc.frequency.value = this.freq;
    this._osc.connect(this._gain);
    this._osc.start();
    this._running = true;
    this._scheduledUntil = this._ctx.currentTime;
    this._scheduleAhead();
  }

  stop() {
    if (!this._running) return;
    this._running = false;
    try {
      if (this._nextScheduleTimer) {
        clearTimeout(this._nextScheduleTimer);
        this._nextScheduleTimer = null;
      }
      // Immediately mute to avoid audible tail.
      if (this._gain && this._ctx) {
        try {
          this._gain.gain.cancelScheduledValues(this._ctx.currentTime);
          this._gain.gain.setValueAtTime(0, this._ctx.currentTime);
        } catch (_e) {
          /* ignore */
        }
      }
      if (this._osc) {
        try {
          this._osc.stop();
        } catch (_e) {
          /* ignore */
        }
        try {
          this._osc.disconnect();
        } catch (_e) {
          /* ignore */
        }
        this._osc = null;
      }
      if (this._gain) {
        try {
          this._gain.disconnect();
        } catch (_e) {
          /* ignore */
        }
        this._gain = null;
      }
      if (this._ctx) {
        try {
          this._ctx.close();
        } catch (_e) {
          /* ignore */
        }
        this._ctx = null;
      }
    } catch (_e) {
      /* ignore */
    }
  }

  _scheduleAhead() {
    if (!this._running || !this._ctx || !this._gain) return;
    const unit = this.unitMs / 1000; // seconds
    // Schedule 2 full loops ahead; wake up again before they finish.
    const scheduleWindow = 2;
    for (let loop = 0; loop < scheduleWindow; loop++) {
      let t = this._scheduledUntil;
      for (const sym of SOS_SYMBOLS) {
        if (sym.tone != null) {
          const dur = sym.tone * unit;
          // tiny 5ms attack/release to avoid clicks
          const atk = Math.min(0.01, dur / 4);
          this._gain.gain.setValueAtTime(0, t);
          this._gain.gain.linearRampToValueAtTime(0.35, t + atk);
          this._gain.gain.setValueAtTime(0.35, t + dur - atk);
          this._gain.gain.linearRampToValueAtTime(0, t + dur);
          t += dur;
        } else {
          const dur = sym.silence * unit;
          this._gain.gain.setValueAtTime(0, t);
          t += dur;
        }
      }
      this._scheduledUntil = t;
    }
    // Wake up a little before we run out of schedule.
    const now = this._ctx.currentTime;
    const msAhead = Math.max(500, (this._scheduledUntil - now - 1) * 1000);
    this._nextScheduleTimer = setTimeout(() => this._scheduleAhead(), msAhead);
  }
}
